import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'http://localhost:3200';

const NAV_LINKS = [
    { label: 'Features', path: '/features' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'Industries', path: '/industries' },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    return (
        <nav
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 50,
                transition: 'all 300ms ease',
                background: scrolled ? 'rgba(8, 9, 26, 0.85)' : 'transparent',
                backdropFilter: scrolled ? 'blur(20px)' : 'none',
                WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
                borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : '1px solid transparent',
            }}
        >
            <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem' }}>
                <div className="flex items-center justify-between" style={{ height: '72px' }}>
                    {/* Logo */}
                    <Link
                        to="/"
                        className="flex items-center gap-2.5"
                        style={{ textDecoration: 'none' }}
                    >
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 10,
                                background: 'linear-gradient(135deg, var(--color-nova), var(--color-plasma))',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                <line x1="12" x2="12" y1="19" y2="22" />
                            </svg>
                        </div>
                        <span
                            className="font-display font-bold"
                            style={{ fontSize: '1.25rem', color: 'var(--color-starlight)' }}
                        >
                            Lumina
                        </span>
                    </Link>

                    {/* Desktop Links */}
                    <div className="hidden md:flex items-center" style={{ gap: '2rem' }}>
                        {NAV_LINKS.map(link => (
                            <Link
                                key={link.path}
                                to={link.path}
                                style={{
                                    textDecoration: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: location.pathname === link.path
                                        ? 'var(--color-starlight)'
                                        : 'var(--color-stardust)',
                                    transition: 'color 200ms',
                                }}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    {/* Desktop CTA */}
                    <div className="hidden md:flex items-center" style={{ gap: '1rem' }}>
                        <a
                            href={`${PORTAL_URL}/login`}
                            style={{
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: 'var(--color-stardust)',
                                transition: 'color 200ms',
                            }}
                        >
                            Sign In
                        </a>
                        <a
                            href={`${PORTAL_URL}/signup`}
                            className="btn-primary"
                            style={{
                                padding: '0.5rem 1.25rem',
                                fontSize: '0.8125rem',
                                borderRadius: '10px',
                            }}
                        >
                            Start Free Trial
                            <ArrowRight style={{ width: 14, height: 14 }} />
                        </a>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <button
                        className="md:hidden"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-starlight)',
                            cursor: 'pointer',
                            padding: '0.5rem',
                        }}
                    >
                        {mobileOpen ? <X style={{ width: 24, height: 24 }} /> : <Menu style={{ width: 24, height: 24 }} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {mobileOpen && (
                <div
                    className="md:hidden"
                    style={{
                        background: 'rgba(8, 9, 26, 0.95)',
                        backdropFilter: 'blur(20px)',
                        borderTop: '1px solid rgba(255,255,255,0.06)',
                        padding: '1.5rem',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {NAV_LINKS.map(link => (
                            <Link
                                key={link.path}
                                to={link.path}
                                style={{
                                    textDecoration: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    color: location.pathname === link.path
                                        ? 'var(--color-starlight)'
                                        : 'var(--color-stardust)',
                                    padding: '0.5rem 0',
                                }}
                            >
                                {link.label}
                            </Link>
                        ))}
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '1rem', marginTop: '0.5rem' }}>
                            <a
                                href={`${PORTAL_URL}/signup`}
                                className="btn-primary"
                                style={{ width: '100%', justifyContent: 'center', padding: '0.75rem' }}
                            >
                                Start Free Trial
                                <ArrowRight style={{ width: 16, height: 16 }} />
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
}
